import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Dumbbell, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { parseDecimal } from "@/lib/decimal";
import { trainingApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";
import { gearSummary } from "./settings/gear";

// backend/training/constants.py MAX_BODYWEIGHT.
const MAX_BODYWEIGHT = 700;

/** The browser's zone, if it has one. */
function browserZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Every IANA zone this browser knows, plus UTC and whatever is chosen now. */
function zoneList(current) {
  let zones;
  try {
    zones = Intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    zones = []; // an older browser: the current zone and UTC still list
  }
  const all = new Set([...zones, "UTC"]);
  if (current) all.add(current);
  return [...all].sort();
}

/**
 * The first visit (walkthrough 1): what Training is, in the spec's words,
 * the gym it's set up for (editable in Settings), and — once — your
 * bodyweight (for chin-ups) and time zone (prefilled from this browser).
 * *Start today's session* saves those two (PATCH settings) and moves on to
 * the check-in; `onContinue(settings)` gets the saved settings (or the
 * unchanged ones when nothing needed asking).
 *
 * `firstSession` false: history came first (through an agent), but the
 * settings were never saved — the time zone is still the default's.
 */
function TrainingIntro({ settings, onContinue, firstSession = true }) {
  const id = useId();
  // Asks once (f14): until the settings are first saved — here or in
  // Settings. A bodyweight left empty then isn't asked for again; the
  // intro says where to add it.
  const ask = !settings.saved;
  const noBodyweight = settings.bodyweight === null || settings.bodyweight === undefined;
  const [zone, setZone] = useState(() =>
    settings.saved ? settings.timezone : (browserZone() ?? settings.timezone ?? "UTC"),
  );
  const [bodyweight, setBodyweight] = useState(
    settings.bodyweight === null || settings.bodyweight === undefined ? "" : String(settings.bodyweight),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const zones = useMemo(() => zoneList(zone), [zone]);
  const units = settings.units ?? "lb";

  const fieldError = (name) => {
    const value = fieldErrors[name];
    return value ? [].concat(value).join(" ") : null;
  };

  const start = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!ask) {
      onContinue(settings);
      return;
    }
    const body = { timezone: zone };
    if (bodyweight.trim() !== "") {
      const value = parseDecimal(bodyweight);
      if (value === null || value <= 0 || value > MAX_BODYWEIGHT) {
        setFieldErrors({ bodyweight: [`Type your weight in ${units}, e.g. 185 — or leave it empty.`] });
        return;
      }
      // the API keeps two decimals (DecimalField): say so in Settings' words
      if (Math.abs(value * 100 - Math.round(value * 100)) > 1e-6) {
        setFieldErrors({ bodyweight: ["Use at most two decimals, like 182.25."] });
        return;
      }
      body.bodyweight = value;
    }
    setSaving(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const { data } = await trainingApi.settings.patch(body);
      onContinue(data);
    } catch (err) {
      const parsed = parseApiError(err, "Couldn't save your settings.");
      setFieldErrors(parsed.fieldErrors);
      setFormError(parsed.formError);
      setSaving(false);
    }
  };

  const weightError = fieldError("bodyweight");
  const zoneError = fieldError("timezone");

  return (
    <section aria-labelledby={`${id}-title`} className="rounded-lg border bg-card p-4 md:p-6">
      {/* focused when Today moves here (a1) */}
      <h2
        id={`${id}-title`}
        tabIndex={-1}
        data-phase-heading
        className="m-0 mb-2 font-display text-xl font-semibold outline-none"
      >
        {firstSession ? "Your first session" : "Before today's session"}
      </h2>
      <p className="m-0 text-muted-foreground">
        Each time, a short check-in (or skip it), then a plan with the reason for every number. Log
        each set in a tap or two; the next plan follows what you did. Suggestions only — change any
        of it.
      </p>
      {/* The link on a row of its own, a 44 px phone target: inside the
          sentence it wrapped into a two-line, 37 px one. */}
      <div className="mt-3 flex flex-col items-start text-sm">
        <p className="m-0 text-muted-foreground">Set up for your gym: {gearSummary(settings.equipment)}.</p>{" "}
        <Button asChild variant="link" className="h-11 p-0 md:h-auto">
          <Link to="/training/settings">Change it in Settings</Link>
        </Button>
      </div>
      {!ask && noBodyweight && (
        <p className="m-0 text-sm text-muted-foreground">
          No bodyweight saved: chin-ups progress by reps and bands. Add it in Settings any time.
        </p>
      )}
      <form onSubmit={start} noValidate className="mt-4 flex flex-col gap-4">
        {ask && (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor={`${id}-bw`}>Your bodyweight ({units}, for chin-ups)</Label>
              <Input
                id={`${id}-bw`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                enterKeyHint="next"
                placeholder="e.g. 185"
                value={bodyweight}
                disabled={saving}
                className="h-11 max-w-48 tabular-nums md:h-9"
                aria-invalid={weightError ? true : undefined}
                aria-describedby={weightError ? `${id}-bw-error` : `${id}-bw-help`}
                onChange={(event) => setBodyweight(event.target.value)}
              />
              {weightError ? (
                <p id={`${id}-bw-error`} className="m-0 text-sm text-destructive">
                  {weightError}
                </p>
              ) : (
                <p id={`${id}-bw-help`} className="m-0 text-xs text-muted-foreground">
                  A chin-up&apos;s load is your bodyweight plus added weight, minus a band&apos;s
                  help. Leave it empty to skip — chin-ups then progress by reps and bands.
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${id}-tz`}>Your time zone</Label>
              <NativeSelect
                id={`${id}-tz`}
                value={zone}
                disabled={saving}
                className="h-11 md:h-9"
                aria-invalid={zoneError ? true : undefined}
                aria-describedby={zoneError ? `${id}-tz-error` : `${id}-tz-help`}
                onChange={(event) => setZone(event.target.value)}
              >
                {zones.map((name) => (
                  <option key={name} value={name}>
                    {name.replace(/_/g, " ")}
                  </option>
                ))}
              </NativeSelect>
              {zoneError ? (
                <p id={`${id}-tz-error`} className="m-0 text-sm text-destructive">
                  {zoneError}
                </p>
              ) : (
                <p id={`${id}-tz-help`} className="m-0 text-xs text-muted-foreground">
                  From this browser — check it. History&apos;s days start at midnight here.
                </p>
              )}
            </div>
          </>
        )}
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <div>
          <Button type="submit" className="h-11 w-full md:h-10 md:w-auto" disabled={saving}>
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Dumbbell aria-hidden="true" />}
            {saving ? "Saving…" : "Start today's session"}
          </Button>
          <p className="mt-2 mb-0 text-sm text-muted-foreground">
            {ask
              ? "Saves these to your settings, then: a short check-in and today's plan."
              : "Next: a short check-in (or skip it), then today's plan."}
          </p>
        </div>
      </form>
    </section>
  );
}

TrainingIntro.propTypes = {
  /** TrainingSettings (from the Training layout). */
  settings: PropTypes.shape({
    saved: PropTypes.bool,
    units: PropTypes.string,
    timezone: PropTypes.string,
    bodyweight: PropTypes.number,
    equipment: PropTypes.object,
  }).isRequired,
  /** Called with the settings to use from here (saved, or as they were). */
  onContinue: PropTypes.func.isRequired,
  /** False when there's history already (an agent's) — the heading says so. */
  firstSession: PropTypes.bool,
};

export default TrainingIntro;
